import type { DataProvider } from 'react-admin';
import type { Supplier, VatInvoice } from '../types';
import { invoiceIdentityKey, isImportableMisaInvoice } from './misaInvoiceSync';
import { fetchAllMeInvoices, fetchAllMeSuppliers, mapMeInvoiceRow, mapMeSupplierRow } from './meInvoiceClient';

export interface MeInvoiceSyncSummary { total: number; created: number; updated: number; skipped: number; invalid: number }
export interface MeInvoiceSupplierSyncSummary extends MeInvoiceSyncSummary { verifiedSupplierKeys: string[] }
const digits = (value?: string) => String(value || '').replace(/\D/g, '');
const text = (value?: string) => String(value || '').trim().toLocaleLowerCase('vi-VN').replace(/\s+/g, ' ');
export const meInvoiceSupplierIdentityKey = (item: Pick<Supplier, 'taxCode' | 'name'>) => digits(item.taxCode) || text(item.name);

export async function syncInvoicesFromMeInvoice(
  dp: DataProvider,
  options?: { fromISO?: string; toISO?: string },
): Promise<MeInvoiceSyncSummary> {
  const remote = await fetchAllMeInvoices({ ...options, pageSize: 100 });
  const mapped = remote.rows.map(mapMeInvoiceRow);
  const valid = mapped.filter(isImportableMisaInvoice);
  const { data: local } = await dp.getList<VatInvoice>('vatInvoices', {
    pagination: { page: 1, perPage: 10000 }, sort: { field: 'invoiceDate', order: 'DESC' }, filter: {},
  });
  const byKey = new Map(local.map((item) => [invoiceIdentityKey(item), item]));
  const result = { total: mapped.length, created: 0, updated: 0, skipped: 0, invalid: mapped.length - valid.length };
  for (const invoice of valid) {
    const existing = byKey.get(invoiceIdentityKey(invoice));
    if (!existing) {
      const created = await dp.create<VatInvoice>('vatInvoices', { data: invoice as Omit<VatInvoice, 'id'> });
      byKey.set(invoiceIdentityKey(created.data), created.data);
      result.created++;
    } else if (existing.supplierName === invoice.supplierName && existing.taxCode === invoice.taxCode && existing.invoiceDate === invoice.invoiceDate && existing.amount === invoice.amount && existing.meInvoiceId === invoice.meInvoiceId) {
      result.skipped++;
    } else {
      await dp.update('vatInvoices', { id: existing.id, data: { ...existing, ...invoice }, previousData: existing });
      result.updated++;
    }
  }
  return result;
}

export async function syncSuppliersFromMeInvoice(dp: DataProvider): Promise<MeInvoiceSupplierSyncSummary> {
  const remote = await fetchAllMeSuppliers(100);
  const mapped = remote.rows.map(mapMeSupplierRow);
  const valid = mapped.filter((item) => item.name);
  const { data: local } = await dp.getList<Supplier>('suppliers', {
    pagination: { page: 1, perPage: 10000 }, sort: { field: 'name', order: 'ASC' }, filter: {},
  });
  const byKey = new Map(local.map((item) => [meInvoiceSupplierIdentityKey(item), item]));
  const result: MeInvoiceSupplierSyncSummary = {
    total: mapped.length, created: 0, updated: 0, skipped: 0, invalid: mapped.length - valid.length, verifiedSupplierKeys: [],
  };
  for (const supplier of valid) {
    const identityKey = meInvoiceSupplierIdentityKey(supplier);
    result.verifiedSupplierKeys.push(identityKey);
    const existing = byKey.get(identityKey);
    if (!existing) {
      const created = await dp.create<Supplier>('suppliers', { data: supplier as Omit<Supplier, 'id'> });
      byKey.set(meInvoiceSupplierIdentityKey(created.data), created.data);
      result.created++;
      continue;
    }
    const nonEmpty = Object.fromEntries(Object.entries(supplier).filter(([, value]) => value !== '' && (!Array.isArray(value) || value.length)));
    const merged = { ...existing, ...nonEmpty };
    if (JSON.stringify(merged) === JSON.stringify(existing)) result.skipped++;
    else {
      await dp.update('suppliers', { id: existing.id, data: merged, previousData: existing });
      result.updated++;
    }
  }
  return result;
}
